<template>
  <div class="step-two-table">
    <DxDataGrid
      class="data-grid-products"
      :data-source="[...filteredProducts, ...lastRowWithTotal]"
      :show-borders="true"
    >
      <DxColumn
        data-field="product"
        :caption="$t('treatment.treatmentPlan.treatmentCreatePlan.product')"
        :allow-sorting="false"
        :allow-editing="false"
      />
      <DxColumn
        data-field="agent"
        :caption="$t('treatment.treatmentPlan.treatmentCreatePlan.plagueAgent')"
        :allow-sorting="false"
        :allow-editing="false"
      />
      <DxColumn
        data-field="dose_type"
        :caption="$t('treatment.treatmentPlan.treatmentCreatePlan.dosage')"
        :allow-sorting="false"
        :allow-editing="false"
      />
      <DxColumn
        :caption="
          $t('treatment.treatmentPlan.treatmentCreatePlan.treatmentType')
        "
        cell-template="selectColumnTemplate"
        :min-width="180"
      />

      <template #selectColumnTemplate="{ data }">
        <div v-if="data.data.isLastRow !== true">
          <DxSelectBox
            v-if="!readOnly"
            v-model="data.data.treatment_type"
            :items="treatmentTypes"
          />
          <span v-else>{{ data.data.treatment_type }}</span>
        </div>
      </template>
    </DxDataGrid>

    <DxDataGrid
      class="data-grid-products-data"
      :data-source="[...filteredProducts, ...lastRowWithTotal]"
      :column-min-width="100"
      :column-auto-width="true"
      :show-borders="true"
      :on-cell-prepared="onCellPrepared"
      @row-updated="onRowUpdated"
    >
      <DxColumn
        :caption="$t('unitaries')"
        alignment="center"
      >
        <DxColumn
          data-field="dose_volume"
          alignment="right"
          name="dose_volume_Ha"
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.doseVolumeHa')
          "
          :allow-sorting="false"
          :calculate-display-value="getDoseVolumeValueFirstType"
        />
        <DxColumn
          data-field="dose_volume"
          alignment="right"
          name="dose_volume_L"
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.doseVolumeL')
          "
          :allow-sorting="false"
          :calculate-display-value="getDoseVolumeValueSecondType"
        />
        <DxColumn
          data-field="broth_volume"
          alignment="right"
          :caption="
            $t(
              'treatment.treatmentPlan.treatmentCreatePlan.stepTwo.brothVolumeHa'
            )
          "
          :allow-sorting="false"
        />
        <DxColumn
          data-field="price"
          alignment="right"
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.priceL')
          "
          :allow-sorting="false"
        />
        <DxColumn
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.doseVat')
          "
          alignment="right"
          :allow-sorting="false"
          :allow-editing="false"
          css-class="disabled-column"
          :calculate-cell-value="getDoseVat"
        />
        <DxColumn
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.priceVat')
          "
          alignment="right"
          :allow-sorting="false"
          :allow-editing="false"
          css-class="disabled-column"
          :calculate-cell-value="getPriceBroth"
        />
      </DxColumn>
      <DxColumn
        :caption="$t('totals')"
        alignment="center"
      >
        <DxColumn
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.doseTotal')
          "
          alignment="right"
          :allow-sorting="false"
          :allow-editing="false"
          css-class="disabled-column"
          :calculate-cell-value="getDoseTotal"
        />
        <DxColumn
          :caption="
            $t('treatment.treatmentPlan.treatmentCreatePlan.stepTwo.cost')
          "
          alignment="right"
          :allow-sorting="false"
          :allow-editing="false"
          css-class="disabled-column"
          :calculate-cell-value="getCostValue"
        />
      </DxColumn>
      <DxEditing
        v-if="!readOnly"
        :allow-updating="true"
        mode="cell"
      />
      <DxEditing
        v-if="!readOnly"
        :allow-updating="true"
        mode="cell"
      />
    </DxDataGrid>

    <DxDataGrid
      class="data-grid-buttons"
      :data-source="[...filteredProducts, ...lastRowWithTotal]"
      :show-borders="true"
    >
      <DxColumn
        cell-template="buttonsColumnTemplate"
        alignment="center"
      />

      <template #buttonsColumnTemplate="{ data }">
        <div
          class="column-buttons"
        >
          <DxButton
            v-if="data.data.isLastRow !== true"
            styling-mode="text"
            type="success"
            icon="find"
            @click="viewProduct(data)"
          />

          <DxButton
            v-if="!readOnly && data.data.isLastRow !== true"
            styling-mode="text"
            type="danger"
            icon="trash"
            @click="deleteItem(data)"
          />
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxDataGrid, DxColumn, DxEditing } from 'devextreme-vue/data-grid';
import { mapActions, mapState } from 'vuex';
import DxButton from 'devextreme-vue/button';

export default {
  name: 'TreatmentStepTwoTableVat',
  components: {
    DxSelectBox,
    DxButton,
    DxDataGrid,
    DxColumn,
    DxEditing,
  },
  props: {
    period: {
      type: Object,
      default: () => {},
    },
    selectedIndex: Number,
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ownerAgent: 'TIMAC',
      ownerAgentAll: 'TODOS',
      treatmentTypes: ['FOLIAR', 'RADICULAR'],
    };
  },
  computed: {
    ...mapState('treatmentStepper', ['treatmentPlan']),
    ...mapState('Plantation', ['currentPlantation']),
    ...mapState('RegisterPlantation', ['actualPlantation']),
    filteredProducts() {
      if (this.readOnly) {
        return this.period.products.filter((item) => item.active !== false && item.dose_volume > 0);
      }

      return this.period.products.filter((item) => item.active !== false);
    },
    lastRowWithTotal() {
      return [
        {
          product: '',
          agent: '',
          dose_type: '',
          isLastRow: true,
        },
      ];
    },
    TotalCostOfPeriod() {
      const total = this.filteredProducts.reduce((acc, item) => {
        const cost_ha = this.getCostHaValue(item);

        if (item.isLastRow) {
          return this.TotalCostOfPeriod;
        }

        const cost = this.calculateFormula(4, {
          cost_ha,
          area: this.getArea,
        });

        return acc + cost;
      }, 0);

      const fixedResult = Number(total.toFixed(2));

      if (isNaN(fixedResult) || !isFinite(fixedResult)) {
        return 0;
      }

      return fixedResult;
    },
    getArea() {
      const { value: area } = this.actualPlantation?.farms[0].properties.find(prop => prop.key === "area") || {};
      return this.fixNumberTo(area, 2);
    },
  },
  methods: {
    onRowUpdated(e) {
      const { broth_volume } = e.data;

      this.period.products = this.period.products.map((item) => {
        item.broth_volume = broth_volume;
        return item;
      });

      let indexPeriod = 0;
      let count = this.selectedIndex;

      for (const period of this.treatmentPlan.periods) {
        const doseVolumeNotEmpty = period.products.some(
          ({ dose_volume, active }) => (dose_volume > 0 && active),
        );

        if (doseVolumeNotEmpty) {
          if (count === 0) {
            break;
          }

          count--;
        }
        indexPeriod++;
      }

      this.treatmentPlan.periods[indexPeriod].vats_number = Number(this.calculateFormula(8, {
        broth_volume,
        vat_volume: this.treatmentPlan.vat_volume,
        number_vats_ha: this.period.number_vats_ha,
        area: this.getArea,
      }).toFixed(2));
    },
    deleteItem(item) {
      let indexPeriod = 0;
      let count = this.selectedIndex;

      for (const period of this.treatmentPlan.periods) {
        const doseVolumeNotEmpty = period.products.some(
          ({ dose_volume, active }) => (dose_volume > 0 && active),
        );

        if (doseVolumeNotEmpty) {
          if (count === 0) {
            break;
          }

          count--;
        }
        indexPeriod++;
      }

      const index = item.rowIndex;
      const { products } = this.treatmentPlan.periods[indexPeriod];

      let trueIndex = index;

      for (let i = index; i < products.length; i++) {
        if (products[i].active === false) {
          trueIndex++;
        } else {
          break;
        }
      }

      this.treatmentPlan.periods[indexPeriod].products[
        trueIndex
      ].active = false;
      this.treatmentPlan.periods[indexPeriod].products[
        trueIndex
      ].dose_volume = 0;
    },
    async viewProduct(item) {
      const product = this.treatmentPlan.products.find(product => product.product_id === item.data.product_id);

      if (product.agent === this.ownerAgent) {
        this.$emit('owner', this.ownerAgent);

        await this.getNutritionalProductDetail(product.id);
      } else if (product.agent === this.ownerAgentAll) {
        this.$emit('owner', this.ownerAgentAll);

        await this.getPhytosanitaryProductDetail({
          id: product.id,
          crop: this.currentPlantation.species,
        });
      } else {
        this.$emit('owner', '');

        await this.getPhytosanitaryProductDetail({
          id: product.id,
          crop: this.currentPlantation.species,
          agent: product.agent,
        });
      }

      this.setDisplayProductDetailPopup(true);
    },
    onCellPrepared(e) {
      if (e.rowType === 'data' && e.data.isLastRow === true) {
        e.cellElement.classList.add('disabled-column');
        e.cellElement.style.fontWeight = 'bold';
        e.cellElement.style.pointerEvents = 'none';
        return;
      }

      if (
        e.rowType === 'data'
        && (e.columnIndex === 0 || e.columnIndex === 1)
      ) {
        if (e.columnIndex === 0 && e.data.dose_type === 'kG-L/1000L') {
          e.cellElement.classList.add('disabled-column');
          e.cellElement.style.pointerEvents = 'none';
        }

        if (e.columnIndex === 1 && e.data.dose_type === 'kG-L/HA') {
          e.cellElement.classList.add('disabled-column');
          e.cellElement.style.pointerEvents = 'none';
        }
      }
    },
    getDoseVolumeValueFirstType(data) {
      const type = 'kG-L/HA';

      if (data.dose_type === type) {
        return data.dose_volume;
      }

      return this.calculateFormula(2, data);
    },
    getDoseVolumeValueSecondType(data) {
      const type = 'kG-L/1000L';

      if (data.dose_type === type) {
        return data.dose_volume;
      }

      // return this.calculateFormula(1, data);
      return '';
    },
    getCostHaValue(data) {
      const dose_volume = this.getDoseVolumeValueFirstType(data);

      return this.calculateFormula(3, {
        dose_volume,
        price: data.price,
        isLastRow: data.isLastRow,
      });
    },
    getDoseVat(data) {
      const dose_volume = this.getDoseVolumeValueFirstType(data);
      const vat_volume = data.broth_volume / this.treatmentPlan.vat_volume;

      return this.calculateFormula(6, {
        dose_volume,
        vat_volume,
        isLastRow: data.isLastRow,
      });
    },
    getPriceBroth(data) {
      const dose_volume_cuba = this.getDoseVat(data);

      return this.calculateFormula(7, {
        dose_volume_cuba,
        price: data.price,
        isLastRow: data.isLastRow,
      });
    },
    getCostValue(data) {
      const cost_ha = this.getCostHaValue(data);

      if (data.isLastRow) {
        return this.TotalCostOfPeriod;
      }

      return this.calculateFormula(4, {
        cost_ha,
        area: this.getArea,
        isLastRow: data.isLastRow,
      });
    },
    getDoseTotal(data) {
      const dose_volume = this.getDoseVolumeValueFirstType(data);

      return this.calculateFormula(9, {
        dose_volume,
        area: this.getArea,
        isLastRow: data.isLastRow,
      });
    },
    fixNumberTo(number, digits) {
      return parseFloat(parseFloat(number).toFixed(digits));
    },
    calculateFormula(type, data) {
      if (data.isLastRow === true) {
        return '';
      }

      let result = 0;
      // Dosis (kg-L/1000L)= Dosis (kg-L/ha)/(Vol. Caldo (l/ha)/1000)
      if (type === 1) {
        result = data.dose_volume / (data.broth_volume / 1000);
      }

      // Dosis (kg-L/ha) =Dosis (kg-L/1000L) * (Vol. Caldo (l/ha)/1000)
      if (type === 2) {
        result = data.dose_volume * (data.broth_volume / 1000);
      }

      // Coste/ha = Dosis kg-L/ha * Precio €/Kg-L
      if (type === 3) {
        result = data.dose_volume * data.price;
      }

      // Coste = coste (€/ha) * superficie (ha)
      if (type === 4) {
        result = data.cost_ha * data.area;
      }

      // Vol caldo total = Vol caldo (L/ha) *  Superficie (ha)
      if (type === 5) {
        result = data.broth_volume * data.area;
      }

      // Dosis (kg-L/Cuba) = Dosis (kg-l/ha) / Vol.Caldo (l/ha)
      // Dosis (kg-L/Cuba) = Dosis (kg-l/ha) / Vol.cuba
      if (type === 6) {
        result = data.dose_volume / data.vat_volume;
      }

      // Precio (€/cuba) = precio (€/kg-l) / dosis (kg-l/cuba)
      // Precio (€/cuba) = precio (€/kg-l) * dosis (kg-l/cuba)
      if (type === 7) {
        result = data.price * data.dose_volume_cuba;
      }

      // Nº de cubas = Nº de cubas/ha * Superficie (ha)
      if (type === 8) {
        result = (data.broth_volume / data.vat_volume) * data.area;
      }

      // Dosis kg-L Total = Dosis (kg-l/ha) * Superficie (ha)
      if (type === 9) {
        result = data.dose_volume * data.area;
      }

      const fixedResult = Number(result.toFixed(2));

      if (isNaN(fixedResult) || !isFinite(fixedResult)) {
        return 0;
      }
      return fixedResult;
    },
    ...mapActions('Vademecum', [
      'getPhytosanitaryProductDetail',
      'getNutritionalProductDetail',
    ]),
    ...mapActions('treatment', ['setDisplayProductDetailPopup']),
  },
};
</script>

<style src="./treatmentStepTwoTables.styles.scss" scoped lang="scss"></style>
